import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { CommonModule } from '@angular/common'
import { AlbertOverrideWrapper } from '@shared/components/albert-override-wrapper/albert-override-wrapper'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { distinctUntilChanged } from 'rxjs'
import { MemberForm, ParentCompany } from '../../models/member/member.model'
import { MemberService } from '@features/insured/services/member/member.service'
import { InsuredBaseForm } from '../insured-base-form.directive'
import { take } from 'rxjs/operators'
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete'
import { IftaLabel } from 'primeng/iftalabel'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'

@Component({
  selector: 'albert-member',
  standalone: true,
  imports: [ReactiveFormsModule,
    AlbertInputComponent,
    CommonModule,
    AlbertOverrideWrapper,
    AlbertCheckboxComponent,
    AutoComplete,
    IftaLabel,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent],
  templateUrl: './member.component.html',
  styleUrl: './member.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberComponent extends InsuredBaseForm<MemberForm> implements OnInit {
  public override readonly form = input.required<FormGroup<MemberForm>>()
  public readonly isAddInfoChecked = signal(false)
  public readonly isParentCompanyChecked = signal(false)
  public readonly parentCompanies = signal<string[]>([])
  public override readonly canEdit = computed(() => {
    const cat = this.categoryService.activeCategory()
    if (cat !== null) return this.permissionService.canEdit(cat)
    return false
  })
  private readonly memberService = inject(MemberService)

  constructor() {
    super()
    // Disable via the control instead of [disabled] on the reactive form directive
    effect(() => {
      const ctrl = this.form().controls.parentCompany.controls.localValue
      if (this.canEdit()) ctrl.enable({ emitEvent: false })
      else ctrl.disable({ emitEvent: false })
    })
  }

  public ngOnInit(): void {
    const form = this.form()

    this.setupToggleLogic(form.controls.additionalAddressInformation, this.isAddInfoChecked)
    this.setupToggleLogic(form.controls.parentCompany, this.isParentCompanyChecked)

    form.controls.uuid.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged()
    ).subscribe(() => {
      this.manuallyExpandedFields.set(new Set())
      form.controls.additionalAddressInformation.controls.checked?.setValue(false)
      this.isAddInfoChecked.set(false)
      form.controls.parentCompany.controls.checked?.setValue(false)
      this.isParentCompanyChecked.set(false)
    })
  }

  protected override getFormGroup(): FormGroup<MemberForm> {
    return this.form()
  }

  protected onParentCompaniesSearch(event: AutoCompleteCompleteEvent): void {
    const form = this.form()
    const category = form.controls.category.value
    if (category !== null) {
      this.memberService.getParentCompanies(category, event.query).pipe(take(1)).subscribe(companies => {
        const filteredCompanies = companies.filter((c: ParentCompany) =>
          c.name !== form.controls.name.controls.localValue.value
          && c.name !== form.controls.name.controls.externalValue.value)
        this.parentCompanies.set(filteredCompanies.map(c => c.name))
      })
    }
  }
}
